<script lang="ts">
  import { depthStore, type FrameStore } from '../anim/frames';
  import { canExportVideo, exportDepthZip, exportGif, exportVideo, type VideoFormat } from '../anim/export';
  import {
    generateAnimation,
    planAnimation,
    type AnimResult,
    type Progress,
    type SequenceInput,
  } from '../anim/generate';
  import { currentLimits } from '../anim/limits';
  import { PROCEDURAL_DEFAULTS, anim, persistAnim, type AnimSourceKind } from '../anim/settings.svelte';
  import { DEFAULT_PRESET_AMOUNT, TEXT_PRESETS, type TextPresetKind } from '../anim/textAnim';
  import { naturalCompare, readZip } from '../anim/zip';
  import { estimateMs } from '../eta';
  import { formatDuration, t } from '../i18n/index.svelte';
  import { decodeImage, downloadBlob } from '../image';
  import { assets, persist, studio } from '../state.svelte';
  import type { Depth16, DepthData, ProceduralAnim } from '../types';
  import { batchPool, defaultPoolSize } from '../wasm/pool';
  import DepthPanel from './DepthPanel.svelte';
  import Player from './Player.svelte';
  import StereoPanel from './StereoPanel.svelte';
  import TextFields from './TextFields.svelte';
  import TexturePanel from './TexturePanel.svelte';
  import { probeVideo, type VideoInfo } from '../anim/video';
  import { mlStatus } from '../ml/client.svelte';
  import { runServerAnimation, type ServerResult } from '../anim/server';
  import { estimate as serverEstimateApi } from '../server/api';
  import { computeTarget, serverModels } from '../server/compute.svelte';
  import MediaPreview from './MediaPreview.svelte';
  import PublishDialog from './PublishDialog.svelte';
  import { caps } from '../config.svelte';
  import { videoPreview } from '../gallery/api';
  import { sceneStore } from '../scene/store.svelte';
  import { ui } from '../ui.svelte';
  import FileButton from './ui/FileButton.svelte';
  import Section from './ui/Section.svelte';
  import Select from './ui/Select.svelte';
  import Slider from './ui/Slider.svelte';

  let sequence = $state.raw<SequenceInput | null>(null);
  let morphA = $state.raw<{ name: string; depth: DepthData } | null>(null);
  let morphB = $state.raw<{ name: string; depth: DepthData } | null>(null);

  async function loadMorph(f: File, which: 'a' | 'b') {
    error = '';
    try {
      const depth = f.name.toLowerCase().endsWith('.png')
        ? await batchPool().call('decodeDepthPng', { bytes: new Uint8Array(await f.arrayBuffer()) })
        : await batchPool().call('depthFromImage', { image: await decodeImage(f), heuristic: { kind: 'brightness' } });
      if (which === 'a') morphA = { name: f.name, depth };
      else morphB = { name: f.name, depth };
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    }
  }
  let video = $state.raw<{ file: File; info: VideoInfo } | null>(null);
  let result = $state.raw<AnimResult | null>(null);
  let progress = $state<Progress | null>(null);
  let running = $state(false);
  let exporting = $state<string | null>(null);
  let exportProgress = $state(0);
  let error = $state('');
  let controller: AbortController | null = null;
  let showDepth = $state(false);
  let depthPreview = $state.raw<FrameStore | null>(null);
  let videoSupport = $state<Record<VideoFormat, boolean>>({ webm: false, mp4: false });
  let serverResult = $state.raw<ServerResult | null>(null);
  let queuePosition = $state<number | undefined>(undefined);

  const target = $derived(computeTarget());
  const limits = $derived(currentLimits(target));
  const inputs = $derived({
    sourceImage: assets.sourceImage,
    uploadedDepth: assets.uploadedDepth,
    textureImage: assets.textureImage,
    sequence,
    video,
    morph: { a: morphA?.depth ?? null, b: morphB?.depth ?? null },
  });
  const plan = $derived(planAnimation(anim, inputs, limits));
  const clientEstimate = $derived.by(() => {
    const mpx = (plan.width * plan.height) / 1e6;
    const render = (estimateMs('stereogram', mpx * studio.stereo.oversample) * plan.frames) / defaultPoolSize();
    const depth =
      anim.source === 'text'
        ? estimateMs('text_depth', mpx) * plan.frames
        : anim.source === 'video'
          ? estimateMs(`ml:depth-anything-v2-small:${mlStatus.backend ?? 'wasm'}`, 1) * plan.frames
          : 0;
    return render + depth;
  });
  // Server estimate: queue-aware, from /api/estimate (debounced).
  let serverEta = $state<number | null>(null);
  $effect(() => {
    if (target !== 'server') {
      serverEta = null;
      return;
    }
    const { width, height, frames } = plan;
    const os = studio.stereo.oversample;
    const fmt = anim.serverFormat;
    const src = anim.source;
    const model = studio.serverModel;
    const ctl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const base = { width, height, frames, oversample: os };
        const a = await serverEstimateApi({ kind: 'animation', format: fmt, ...base }, ctl.signal);
        let total = a.eta_ms;
        if (src === 'video' && video) {
          const v = await serverEstimateApi(
            { kind: 'video_depth', model, width: video.info.width, height: video.info.height, frames },
            ctl.signal,
          );
          total += v.eta_ms;
        }
        serverEta = total;
      } catch {
        serverEta = null;
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  });
  const estimate = $derived(target === 'server' && serverEta !== null ? serverEta : clientEstimate);
  const canRun = $derived(
    anim.source === 'sequence'
      ? !!sequence
      : anim.source === 'video'
        ? !!video
        : anim.source === 'morph'
          ? !!morphB
          : true,
  );

  $effect(() => {
    JSON.stringify(anim);
    persistAnim();
  });
  $effect(() => {
    JSON.stringify(studio);
    persist();
  });

  $effect(() => {
    // Depth frames may be at source size; preview them as they are.
    depthPreview = showDepth && result ? depthStore(result.depth) : null;
  });

  $effect(() => {
    const r = result;
    if (!r) return;
    for (const f of ['webm', 'mp4'] as VideoFormat[]) {
      canExportVideo(f, r.store.width, r.store.height, r.fps).then(
        (ok) => (videoSupport = { ...videoSupport, [f]: ok }),
      );
    }
  });

  async function onVideo(f: File) {
    error = '';
    try {
      video = { file: f, info: await probeVideo(f) };
    } catch (e) {
      video = null;
      error = t('anim.videoError', { msg: e instanceof Error ? e.message : String(e) });
    }
  }

  async function onZip(f: File) {
    error = '';
    try {
      const entries = (await readZip(await f.arrayBuffer()))
        .filter((e) => e.name.toLowerCase().endsWith('.png'))
        .sort((a, b) => naturalCompare(a.name, b.name))
        .slice(0, limits.maxFrames);
      if (!entries.length) throw new Error(t('anim.noPngInZip'));
      const pool = batchPool();
      const frames: Depth16[] = await Promise.all(entries.map((e) => pool.call('decodeDepthPng16', { bytes: e.data })));
      if (frames.some((fr) => fr.width !== frames[0].width || fr.height !== frames[0].height)) {
        throw new Error(t('anim.mixedSizes'));
      }
      sequence = { frames, name: f.name };
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    }
  }

  async function generate() {
    error = '';
    running = true;
    result = null;
    if (serverResult) URL.revokeObjectURL(serverResult.url);
    serverResult = null;
    controller = new AbortController();
    try {
      if (target === 'server') {
        serverResult = await runServerAnimation(
          $state.snapshot(anim),
          $state.snapshot(studio),
          inputs,
          limits,
          anim.serverFormat,
          studio.serverModel,
          (p) => {
            progress = p;
            queuePosition = p.queuePosition;
          },
          controller.signal,
        );
        return;
      }
      result = await generateAnimation(
        $state.snapshot(anim),
        $state.snapshot(studio),
        inputs,
        limits,
        (p) => (progress = p),
        controller.signal,
      );
    } catch (e) {
      if (
        !(e instanceof DOMException && e.name === 'AbortError') &&
        !(e instanceof Error && e.name === 'CancelledError')
      ) {
        error = e instanceof Error ? e.message : String(e);
      }
    } finally {
      running = false;
      progress = null;
      controller = null;
    }
  }

  function cancel() {
    controller?.abort();
  }

  async function runExport(kind: 'gif' | VideoFormat | 'depth') {
    if (!result) return;
    exporting = kind;
    exportProgress = 0;
    error = '';
    const onP = (d: number, n: number) => (exportProgress = d / n);
    const base = `stereogram-${anim.source}-${studio.stereo.seed}`;
    try {
      if (kind === 'gif') downloadBlob(await exportGif(result.store, result.fps, result.loop, onP), `${base}.gif`);
      else if (kind === 'depth') downloadBlob(await exportDepthZip(result.depth, onP), `${base}-depth16.zip`);
      else downloadBlob(await exportVideo(result.store, result.fps, result.loop, kind, onP), `${base}.${kind}`);
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      exporting = null;
    }
  }

  let publishOpen = $state(false);

  /** File for the gallery: the server result, or a GIF of the client frames. */
  async function preparePublish(): Promise<{ file: Blob; name: string; preview?: Blob }> {
    if (serverResult) {
      const preview = serverResult.format === 'gif' ? undefined : await videoPreview(serverResult.blob);
      return { file: serverResult.blob, name: `animation.${serverResult.format}`, preview };
    }
    if (!result) throw new Error('nothing to publish');
    const gif = await exportGif(result.store, result.fps, result.loop, (d, n) => (exportProgress = d / n));
    return { file: gif, name: 'animation.gif' };
  }

  function shareableParams() {
    const a = $state.snapshot(anim);
    const s = $state.snapshot(studio);
    return {
      source: a.source,
      animation: { ...a, source: a.source },
      text: a.source === 'text' ? s.text : undefined,
      depth: s.depth,
      texture: s.texture,
      stereo: s.stereo,
    };
  }

  function setSource(k: AnimSourceKind) {
    anim.source = k;
  }

  function setPreset(k: TextPresetKind) {
    anim.preset = { kind: k, amount: DEFAULT_PRESET_AMOUNT[k], cycles: 1 };
  }

  function setProcedural(k: ProceduralAnim['kind']) {
    anim.procedural = { ...PROCEDURAL_DEFAULTS[k] };
  }

  const stageLabel = $derived(
    progress
      ? t(
          progress.stage === 'depth'
            ? 'anim.stage.depth'
            : progress.stage === 'smooth'
              ? 'anim.stage.smooth'
              : 'anim.stage.render',
        )
      : '',
  );
  const fix2 = (v: number) => v.toFixed(2);
  const presetUsesAmount = $derived(!['turn', 'spin'].includes(anim.preset.kind));
</script>

<div class="studio">
  <aside class="controls">
    <Section title={t('anim.source')}>
      <div class="seg" role="tablist">
        {#each [['text', t('source.text')], ['procedural', t('anim.procedural')], ['video', t('anim.video')], ['scene', t('anim.scene')], ['morph', t('anim.morph')], ['sequence', t('anim.sequence')]] as [k, label] (k)}
          <button
            class:on={anim.source === k}
            onclick={() => setSource(k as AnimSourceKind)}
            data-testid={`anim-source-${k}`}>{label}</button
          >
        {/each}
      </div>

      {#if anim.source === 'scene'}
        <p class="muted small">
          {t('anim.sceneHint', { d: sceneStore.current.timeline.duration })}
        </p>
        <button onclick={() => (ui.view = 'scene')}>{t('source.openEditor')}</button>
      {:else if anim.source === 'morph'}
        <p class="muted small">{t('anim.morphHint')}</p>
        <div class="morph">
          <span class="small">A</span>
          {#if morphA}
            <span class="small mono">{morphA.name} · {morphA.depth.width}×{morphA.depth.height}</span>
            <button class="x" onclick={() => (morphA = null)} title={t('common.remove')}>×</button>
          {:else}
            <span class="small muted">{t('anim.morphStudio')}</span>
          {/if}
        </div>
        <FileButton label={t('anim.morphA')} accept="image/*" onfile={(f) => loadMorph(f, 'a')} testid="morph-a" />
        <div class="morph">
          <span class="small">B</span>
          {#if morphB}
            <span class="small mono">{morphB.name} · {morphB.depth.width}×{morphB.depth.height}</span>
          {:else}
            <span class="small muted">—</span>
          {/if}
        </div>
        <FileButton label={t('anim.morphB')} accept="image/*" onfile={(f) => loadMorph(f, 'b')} testid="morph-b" />
      {:else if anim.source === 'text'}
        <label class="field">
          <span>{t('anim.preset')}</span>
          <select
            value={anim.preset.kind}
            onchange={(e) => setPreset((e.currentTarget as HTMLSelectElement).value as TextPresetKind)}
            data-testid="anim-preset"
          >
            {#each TEXT_PRESETS as k (k)}<option value={k}>{t(`anim.preset.${k}`)}</option>{/each}
          </select>
        </label>
        {#if presetUsesAmount}
          <Slider
            label={t('anim.amount')}
            bind:value={anim.preset.amount}
            min={['swing', 'tilt'].includes(anim.preset.kind) ? 5 : 0.02}
            max={['swing', 'tilt'].includes(anim.preset.kind) ? 80 : 1}
            step={['swing', 'tilt'].includes(anim.preset.kind) ? 1 : 0.01}
            format={(v) => (['swing', 'tilt'].includes(anim.preset.kind) ? `${v}°` : fix2(v))}
          />
        {/if}
        <Slider label={t('anim.cycles')} bind:value={anim.preset.cycles} min={1} max={4} />
        <TextFields />
      {:else if anim.source === 'procedural'}
        <div class="seg">
          {#each ['waves', 'noise', 'ripple'] as const as k (k)}
            <button class:on={anim.procedural.kind === k} onclick={() => setProcedural(k)} data-testid={`proc-${k}`}
              >{t(`anim.proc.${k}`)}</button
            >
          {/each}
        </div>
        {@const p = anim.procedural}
        <Slider label={t('anim.amplitude')} bind:value={p.amplitude} min={0.05} max={1} step={0.01} format={fix2} />
        {#if p.kind === 'waves'}
          <Slider label={t('anim.wavelength')} bind:value={p.wavelength} min={0.05} max={1} step={0.01} format={fix2} />
          <Slider label={t('anim.direction')} bind:value={p.direction} min={0} max={360} format={(v) => `${v}°`} />
        {:else if p.kind === 'noise'}
          <Slider label={t('texture.scale')} bind:value={p.scale} min={20} max={400} />
          <Slider label={t('stereo.seed')} bind:value={p.seed} min={0} max={999} />
        {:else}
          <Slider
            label={t('anim.wavelength')}
            bind:value={p.wavelength}
            min={0.03}
            max={0.6}
            step={0.01}
            format={fix2}
          />
          <Slider
            label={t('anim.decay')}
            bind:value={p.decay}
            min={0}
            max={5}
            step={0.1}
            format={(v) => v.toFixed(1)}
          />
          <div class="grid2">
            <Slider label={t('anim.centerX')} bind:value={p.cx} min={0} max={1} step={0.01} format={fix2} />
            <Slider label={t('anim.centerY')} bind:value={p.cy} min={0} max={1} step={0.01} format={fix2} />
          </div>
        {/if}
        <Slider label={t('anim.cycles')} bind:value={p.cycles} min={1} max={6} />
        <Select
          label={t('anim.base')}
          bind:value={anim.proceduralBase}
          options={[
            { value: 'flat', label: t('anim.base.flat') },
            { value: 'studio', label: t('anim.base.studio') },
          ]}
        />
      {:else}
        {#if anim.source === 'video'}
          <FileButton label={t('anim.dropVideo')} accept="video/*" onfile={onVideo} testid="video-input" />
          {#if video}
            <p class="muted small" data-testid="video-info">
              {video.info.name} · {video.info.width}×{video.info.height} · {video.info.duration.toFixed(1)} s
            </p>
            {#if video.info.duration > limits.maxSeconds}
              <p class="muted small">{t('anim.videoTrimmed', { s: limits.maxSeconds })}</p>
            {/if}
          {/if}
          {#if target === 'server'}
            <Select
              label={t('server.model')}
              bind:value={studio.serverModel}
              options={serverModels().map((m) => ({
                value: m.id,
                label: `${m.name ?? m.id}${m.backend ? ` · ${m.backend}` : ''}`,
              }))}
              testid="anim-server-model"
            />
            <p class="muted small">{t('server.mlInfo')}</p>
          {:else}
            <p class="muted small">{t('anim.videoHint')}</p>
          {/if}
        {:else}
          <FileButton label={t('anim.dropZip')} accept=".zip,application/zip" onfile={onZip} testid="zip-input" />
          {#if sequence}
            <p class="muted small">
              {sequence.name} · {sequence.frames.length} · {sequence.frames[0].width}×{sequence.frames[0].height}
            </p>
          {/if}
        {/if}
        <Select
          label={t('anim.filter')}
          bind:value={anim.filter}
          options={[
            { value: 'none', label: t('anim.filter.none') },
            { value: 'ema', label: t('anim.filter.ema') },
            { value: 'median', label: t('anim.filter.median') },
          ]}
        />
        {#if anim.filter === 'ema'}
          <Slider label={t('anim.emaAlpha')} bind:value={anim.emaAlpha} min={0.05} max={1} step={0.05} format={fix2} />
        {:else if anim.filter === 'median'}
          <Slider label={t('anim.medianRadius')} bind:value={anim.medianRadius} min={1} max={4} />
        {/if}
        {#if anim.filter !== 'none'}
          <Slider
            label={t('anim.threshold')}
            bind:value={anim.filterThreshold}
            min={0.02}
            max={0.5}
            step={0.01}
            format={fix2}
            hint={t('anim.thresholdHint')}
          />
        {/if}
      {/if}
    </Section>

    <Section title={t('anim.timing')}>
      <div class="grid2">
        <Slider label={t('anim.fps')} bind:value={anim.fps} min={5} max={limits.maxFps} testid="anim-fps" />
        {#if anim.source === 'text' || anim.source === 'procedural' || anim.source === 'morph'}
          <Slider
            label={t('anim.duration')}
            bind:value={anim.duration}
            min={0.5}
            max={limits.maxSeconds}
            step={0.5}
            format={(v) => `${v} s`}
            testid="anim-duration"
          />
        {/if}
      </div>
      <div class="grid2">
        <label class="field"
          ><span>{t('output.width')}</span><input
            type="number"
            min="64"
            max={limits.maxSide}
            bind:value={anim.width}
            data-testid="anim-width"
          /></label
        >
        <label class="field"
          ><span>{t('output.height')}</span><input
            type="number"
            min="64"
            max={limits.maxSide}
            bind:value={anim.height}
            data-testid="anim-height"
          /></label
        >
      </div>
      <div class="seg">
        <button class:on={anim.loop === 'loop'} onclick={() => (anim.loop = 'loop')}>{t('anim.loop')}</button>
        <button class:on={anim.loop === 'ping_pong'} onclick={() => (anim.loop = 'ping_pong')}
          >{t('anim.pingPong')}</button
        >
      </div>
      <p class="muted mono small">
        {t('anim.plan', { w: plan.width, h: plan.height, n: plan.frames, fps: plan.fps })}
      </p>
      {#if target === 'server'}
        <Select
          label={t('server.format')}
          bind:value={anim.serverFormat}
          options={[
            { value: 'mp4', label: 'MP4 (H.264)' },
            { value: 'webm', label: 'WebM (VP9)' },
            { value: 'gif', label: 'GIF' },
            { value: 'zip', label: 'ZIP (PNG)' },
          ]}
          testid="server-format"
        />
        <p class="muted small">{t('anim.serverLimits', { side: limits.maxSide, s: limits.maxSeconds })}</p>
      {:else}
        <p class="muted small">{t('anim.limits', { side: limits.maxSide, s: limits.maxSeconds })}</p>
      {/if}
    </Section>

    <Section title={t('anim.patternMotion')}>
      <div class="seg">
        {#each ['static', 'boiling', 'scroll'] as const as m (m)}
          <button class:on={anim.motion === m} onclick={() => (anim.motion = m)} data-testid={`motion-${m}`}
            >{t(`anim.motion.${m}`)}</button
          >
        {/each}
      </div>
      <p class="muted small">{t(`anim.motionHint.${anim.motion}`)}</p>
      {#if anim.motion === 'scroll'}
        <Slider
          label={t('anim.scrollSpeed')}
          bind:value={anim.scrollSpeed}
          min={-8}
          max={8}
          step={0.5}
          format={(v) => `${v} px`}
        />
      {/if}
    </Section>

    <DepthPanel />
    <TexturePanel allowSource={anim.source === 'video' && !!video} />
    <StereoPanel />
  </aside>

  <main>
    <div class="bar">
      {#if running}
        <button onclick={cancel} data-testid="anim-cancel">{t('common.cancel')}</button>
      {:else}
        <button class="primary" disabled={!canRun} onclick={generate} data-testid="anim-generate"
          >{t('anim.generate')}</button
        >
        <span class="muted small" data-testid="anim-estimate">{t('eta.about', { t: formatDuration(estimate) })}</span>
        {#if estimate > 60000}
          <span class="small warn" data-testid="anim-slow-hint"
            >{target === 'server' ? t('server.slowHint') : t('anim.slowClientHint')}</span
          >
        {/if}
      {/if}
      <div class="status">
        {#if progress}
          <div class="progress" data-testid="anim-progress">
            <div class="fill" style:width={`${(progress.done / Math.max(1, progress.total)) * 100}%`}></div>
          </div>
          <span class="mono small">
            {stageLabel}
            {progress.done}/{progress.total} · {t('anim.left', { t: formatDuration(progress.etaMs) })}
            {#if target === 'server' && queuePosition}· {t('server.queued', { n: queuePosition })}{/if}
          </span>
        {:else if error}
          <span class="error small" data-testid="anim-error">{error}</span>
        {:else if serverResult}
          <span class="muted mono small" data-testid="anim-done">
            {t('anim.serverDone', { n: serverResult.frames, t: formatDuration(serverResult.ms) })}
          </span>
        {:else if result}
          <span class="muted mono small" data-testid="anim-done">
            {t('anim.done', {
              n: result.store.count,
              t: formatDuration(result.ms),
              mb: (result.store.bytes() / 1e6).toFixed(0),
            })}
          </span>
        {/if}
      </div>
      {#if serverResult && !running}
        <button
          class="primary"
          data-testid="server-download"
          onclick={() =>
            serverResult &&
            downloadBlob(serverResult.blob, `stereogram-${anim.source}-${studio.stereo.seed}.${serverResult.format}`)}
          >{t('server.download')} {serverResult.format.toUpperCase()}</button
        >
      {/if}
      {#if caps.gallery && !running && (result || (serverResult && serverResult.format !== 'zip'))}
        <button onclick={() => (publishOpen = true)} data-testid="publish">{t('publish.button')}</button>
      {/if}
      {#if result && !running}
        <div class="row exports">
          {#if exporting}
            <span class="mono small"
              >{t('anim.exporting', { what: exporting.toUpperCase(), p: Math.round(exportProgress * 100) })}</span
            >
          {:else}
            <button class="primary" onclick={() => runExport('gif')} data-testid="export-gif">GIF</button>
            <button
              disabled={!videoSupport.webm}
              onclick={() => runExport('webm')}
              title={videoSupport.webm ? '' : t('anim.noCodec')}
              data-testid="export-webm">WebM</button
            >
            <button
              disabled={!videoSupport.mp4}
              onclick={() => runExport('mp4')}
              title={videoSupport.mp4 ? '' : t('anim.noCodec')}
              data-testid="export-mp4">MP4</button
            >
            <button onclick={() => runExport('depth')} title={t('anim.depthZipHint')}>{t('anim.depthZip')}</button>
          {/if}
        </div>
      {/if}
    </div>
    <div class="playerwrap">
      {#if serverResult}
        <MediaPreview result={serverResult} />
      {:else if result}
        <Player store={depthPreview ?? result.store} fps={result.fps} loop={result.loop}>
          <div class="seg">
            <button class:on={!showDepth} onclick={() => (showDepth = false)}>{t('preview.stereogram')}</button>
            <button class:on={showDepth} onclick={() => (showDepth = true)} data-testid="player-depth"
              >{t('preview.depth')}</button
            >
          </div>
        </Player>
      {:else}
        <div class="empty muted">{running ? t('preview.rendering') : t('anim.empty')}</div>
      {/if}
    </div>
  </main>
</div>

<PublishDialog bind:open={publishOpen} prepare={preparePublish} params={shareableParams()} />

<style>
  .studio {
    display: grid;
    grid-template-columns: 340px 1fr;
    height: 100%;
    min-height: 0;
  }
  .controls {
    background: var(--panel);
    border-right: 1px solid var(--line);
    overflow-y: auto;
  }
  main {
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--line);
    background: var(--panel);
    flex-wrap: wrap;
    min-height: 51px;
  }
  .status {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 200px;
  }
  .progress {
    width: 160px;
    height: 6px;
    background: var(--line);
    border-radius: 3px;
    overflow: hidden;
  }
  .fill {
    height: 100%;
    background: var(--accent);
    transition: width 0.2s;
  }
  .playerwrap {
    flex: 1;
    min-height: 0;
  }
  .empty {
    height: 100%;
    display: grid;
    place-items: center;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 13px;
  }
  .small {
    font-size: 12px;
    margin: 0;
  }
  .warn {
    color: var(--accent);
  }
  .morph {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .morph .x {
    padding: 0 6px;
  }
  @media (max-width: 820px) {
    .studio {
      grid-template-columns: 1fr;
      height: auto;
    }
    .playerwrap {
      height: 70vh;
    }
  }
</style>
